import { SportIDs } from 'types/enums';
import { MatchOffer, ResultScores } from 'types/MatchOffer';
import { getFullTimeOdds } from './matchHelper';

export type ResultsScoreColumns = {
  ft: string;
  ht: string;
  ot: string;
  tip: string;
  odd: string;
};

const SCORE_PAIR = /^(\d+)\s*-\s*(\d+)$/;

function normalizeColon(s: string): string {
  return s.trim().replace(/\s*-\s*/g, ':');
}

function parsePair(part: string): { h: number; a: number } | null {
  const m = part.trim().match(SCORE_PAIR);
  if (!m) return null;
  return { h: Number.parseInt(m[1], 10), a: Number.parseInt(m[2], 10) };
}

/**
 * Derive FT / HT / OT display strings from `Event.ss` and sport.
 * Soccer often sends "1-1,2-1" (HT, FT). Other sports usually a single "x-y".
 */
export function parseSsForResultsColumns(
  ss: string | null | undefined,
  sportId: number
): { ft: string; ht: string; ot: string } {
  if (ss == null || String(ss).trim() === '') {
    return { ft: '-', ht: '-', ot: '-' };
  }
  const raw = String(ss).trim();

  if (sportId === SportIDs.TENNIS) {
    return {
      ft: normalizeColon(raw.replace(/\s*,\s*/g, ', ')),
      ht: '-',
      ot: '-',
    };
  }

  const segments = raw.split(',').map((x) => x.trim());
  if (
    sportId === SportIDs.SOCCER &&
    segments.length >= 2 &&
    parsePair(segments[0]) &&
    parsePair(segments[segments.length - 1])
  ) {
    const ht = normalizeColon(segments[0]);
    const ft = normalizeColon(segments[segments.length - 1]);
    let ot = '-';
    if (segments.length > 2) {
      const mid = segments.slice(1, -1).map(normalizeColon).join(', ');
      if (mid) ot = mid;
    }
    return { ft, ht, ot };
  }

  const single = parsePair(raw);
  if (single) {
    return { ft: normalizeColon(raw), ht: '-', ot: '-' };
  }

  return { ft: raw, ht: '-', ot: '-' };
}

type Pair = { h: number; a: number };

function scorePair(scores: ResultScores, key: string): Pair | null {
  const p = scores[key];
  if (!p) return null;
  const h = Number.parseInt(String(p.home ?? ''), 10);
  const a = Number.parseInt(String(p.away ?? ''), 10);
  return Number.isFinite(h) && Number.isFinite(a) ? { h, a } : null;
}

const show = (p: Pair | null) => (p ? `${p.h}:${p.a}` : '-');

/**
 * FT / HT / OT columns, and the regular-time score the 1X2 tip is read from,
 * from BetsAPI's per-period "scores" (keys per sport, checked on stored
 * results). Null when the sport is not mapped or the scores are missing;
 * then "ss" is used. "ss" alone is one total: a 2-2 won 4-3 on penalties
 * reads "6-5", which showed FT 6:5 and tip 1 instead of 2:2, X, AP 4:3.
 */
export function columnsFromScores(
  scores: ResultScores | undefined,
  sportId: number
): { ft: string; ht: string; ot: string; regular: Pair | null } | null {
  if (!scores || Object.keys(scores).length === 0) return null;
  const s = (k: string) => scorePair(scores, k);
  switch (sportId) {
    case SportIDs.SOCCER: {
      // 1 = half time, 2 = after 90 minutes, 3 = after extra time,
      // 4 = penalty shoot-out.
      const regular = s('2');
      if (!regular) return null;
      const extra: string[] = [];
      if (s('3')) extra.push(`OT ${show(s('3'))}`);
      if (s('4')) extra.push(`AP ${show(s('4'))}`);
      return {
        ft: show(regular),
        ht: show(s('1')),
        ot: extra.length ? extra.join(', ') : '-',
        regular,
      };
    }
    case SportIDs.BASKETBALL: {
      // 1, 2, 4, 5 = quarters, 3 = half time, 6 = overtime, 7 = total.
      const total = s('7');
      if (!total) return null;
      const ot = s('6');
      return {
        ft: show(total),
        ht: show(s('3')),
        ot: ot ? `OT ${show(ot)}` : '-',
        regular: ot ? { h: total.h - ot.h, a: total.a - ot.a } : total,
      };
    }
    case SportIDs.ICE_HOCKEY: {
      // 1-3 = periods, 4 = overtime, 5 = total.
      const total = s('5');
      const periods = ['1', '2', '3'].map(s);
      if (!total || periods.some((p) => !p)) return null;
      const regular = periods.reduce<Pair>(
        (sum, p) => ({ h: sum.h + p!.h, a: sum.a + p!.a }),
        { h: 0, a: 0 }
      );
      const ot = s('4');
      return {
        ft: show(total),
        ht: periods.map(show).join(', '),
        ot: ot ? `OT ${show(ot)}` : '-',
        regular,
      };
    }
    case SportIDs.HANDBALL: {
      // 1 = first half, 2 = second half on its own, 4 = total.
      const total = s('4');
      if (!total) return null;
      return { ft: show(total), ht: show(s('1')), ot: '-', regular: total };
    }
    default:
      return null;
  }
}

function outcomeTipFromPair(
  h: number,
  a: number,
  allowDraw: boolean
): '1' | 'X' | '2' {
  if (h > a) return '1';
  if (a > h) return '2';
  return allowDraw ? 'X' : '1';
}

function ftPairFromMatch(
  match: MatchOffer,
  sportId: number
): { h: number; a: number } | null {
  const { ft } = parseSsForResultsColumns(match.ss, sportId);
  if (ft === '-' || !ft) return null;
  const lastSegment = ft.includes(',')
    ? ft.split(',').pop()!.trim()
    : ft.trim();
  const normalized = lastSegment.replace(/:/g, '-');
  return parsePair(normalized);
}

export function computeResultsTip(match: MatchOffer, sportId: number): string {
  // 1X2 is settled on regular time, so the tip is read from it.
  const pair =
    columnsFromScores(match.scores, sportId)?.regular ??
    ftPairFromMatch(match, sportId);
  if (!pair) return '-';
  const allowDraw =
    sportId === SportIDs.SOCCER ||
    sportId === SportIDs.ICE_HOCKEY ||
    sportId === SportIDs.HANDBALL;
  return outcomeTipFromPair(pair.h, pair.a, allowDraw);
}

export function computeResultsWinningOdd(
  match: MatchOffer,
  tip: string
): string {
  if (tip === '-') return '-';
  const { homeWin, draw, awayWin } = getFullTimeOdds(match);
  const odd =
    tip === '1'
      ? homeWin?.odds
      : tip === 'X'
        ? draw?.odds
        : tip === '2'
          ? awayWin?.odds
          : undefined;
  if (odd == null || Number.isNaN(Number(odd))) return '-';
  return Number(odd).toFixed(2);
}

export function buildResultsRowDisplay(
  match: MatchOffer,
  sportId: number
): ResultsScoreColumns {
  const fromScores = columnsFromScores(match.scores, sportId);
  const { ft, ht, ot } =
    fromScores ?? parseSsForResultsColumns(match.ss, sportId);
  const tip = computeResultsTip(match, sportId);
  const odd = computeResultsWinningOdd(match, tip);
  return { ft, ht, ot, tip, odd };
}

export type ResultsStatusFilter = 'all' | 'ended' | 'live' | 'off';
export type ResultsTipFilter = 'all' | '1' | 'X' | '2';

export type ResultsFilters = {
  /** Local day key (YYYY-MM-DD) or 'all'. */
  day: string;
  status: ResultsStatusFilter;
  tip: ResultsTipFilter;
  /** Part of a team name, any case. */
  team: string;
};

export const NO_RESULTS_FILTERS: ResultsFilters = {
  day: 'all',
  status: 'all',
  tip: 'all',
  team: '',
};

/** BetsAPI time_status: 1 in play, 2 being corrected, 3 ended; the rest are off. */
const statusOf = (match: MatchOffer): ResultsStatusFilter => {
  const ts = Number(match.time_status);
  if (ts === 3) return 'ended';
  if (ts === 1 || ts === 2) return 'live';
  if (Number.isFinite(ts) && ts > 3) return 'off';
  return 'all';
};

/** A match's local day as YYYY-MM-DD. */
export const localDayKey = (unix: number) => {
  const d = new Date(unix * 1000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const fold = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export function resultMatchesFilters(
  match: MatchOffer,
  sportId: number,
  f: ResultsFilters
): boolean {
  if (f.day !== 'all' && localDayKey(match.time) !== f.day) return false;
  if (f.status !== 'all' && statusOf(match) !== f.status) return false;
  if (f.tip !== 'all' && computeResultsTip(match, sportId) !== f.tip) {
    return false;
  }
  const q = fold(f.team.trim());
  if (q) {
    const names = fold(`${match.home?.name ?? ''} ${match.away?.name ?? ''}`);
    if (!names.includes(q)) return false;
  }
  return true;
}

/** Leagues with only the matches the filters let through (empty ones dropped). */
export function filterResultsBlocks<
  B extends { matches: MatchOffer[]; sportId: number },
>(blocks: B[], f: ResultsFilters): B[] {
  return blocks
    .map((b) => ({
      ...b,
      matches: b.matches.filter((m) => resultMatchesFilters(m, b.sportId, f)),
    }))
    .filter((b) => b.matches.length > 0);
}
